/**
 * bridge/notification_token_controller.js — "notification-token" bridge component.
 *
 * Capability-tier bridge component: requests push-notification permission, gets the
 * device push token, and hands it to the web layer so the page can POST it to the
 * server (device registration). Stimulus identifier "bridge--notification-token"
 * (glob-derived from this subdir); Strada component name "notification-token" MUST
 * match the iOS class var name
 * (native/ios/Chippie/Bridge/NotificationTokenComponent.swift).
 *
 * Why it extends Strada's BridgeComponent: the iOS token is reached via
 * this.send("get", ...), which only works on a connected bridge component inside
 * the iOS Hotwire Native shell. Extending BridgeComponent gives us that send()
 * transport AND the UA-gated connect() (it only connects in a native shell).
 *
 * Three-way transport via the window.ChippieBridge facade. On connect this
 * controller registers a single notificationToken() impl set ({ ios, android, pwa })
 * onto the facade. The facade (chippie-bridge.js) owns platform detection and picks
 * the right impl per call. Each impl returns a Promise that resolves to
 * { token, environment } — environment is 'sandbox' | 'production' on iOS
 * (which APNs host the token is valid against, reported by the shell's build
 * configuration) and null on Android/PWA — or null when no token is available,
 * or rejects on denial/failure. Callers do:
 *
 *   const result = await window.ChippieBridge.notificationToken(options);
 *   // result && POST result.token to the server (device registration endpoint)
 *
 * options.interactive (default false) controls what an UNDECIDED permission
 * does: false = never prompt (the silent page-load registration path rejects
 * TOKEN_NOT_DETERMINED); true = the OS permission prompt may fire. Whether and
 * when to ask is the caller's policy (push_registration_controller.js), so
 * interactive is opt-in, never the default.
 *
 * Rejection vocabulary is platform-neutral: TOKEN_NOT_DETERMINED and
 * TOKEN_DENIED, exported below. Each shell words these states differently, and
 * this file is the only place that knows either wording, so callers never
 * branch on a platform. Any other rejection (unprovisioned build, FCM failure)
 * passes through as the shell reported it.
 *
 *   - ios:     this.send("get", {interactive}, msg => ...) over Strada to the Swift
 *              component; the reply carries { token, environment } or { error }.
 *   - android: window.ChippieNative.requestNotificationToken(requestId); the native
 *              @JavascriptInterface is fire-and-forget (it cannot return async), so
 *              it resolves back through window.ChippieBridge._resolveNotificationToken
 *              keyed by requestId once FCM hands back the token (or denial/error).
 *              That call shows the Android 13+ dialog whenever the grant is
 *              missing and has no silent mode, so a non-interactive request checks
 *              the grant first through the permissions bridge and never reaches it
 *              ungranted. Android cannot tell "never asked" from "denied" without
 *              prompting, so both report TOKEN_NOT_DETERMINED there; the interactive
 *              request that follows reveals which it was.
 *   - pwa:     no device push token without a full Web Push + service-worker +
 *              VAPID setup, which is out of scope here; resolves null so callers can
 *              treat "no native token" uniformly rather than throwing on desktop.
 *
 * Template wiring (either path):
 *   <div data-controller="bridge--notification-token">
 *     <button data-action="bridge--notification-token#get">Enable notifications</button>
 *   </div>
 *   <!-- listen for the resolved token: -->
 *   data-action="bridge--notification-token:retrieved@window->my-controller#retrieved"
 * Or call window.ChippieBridge.notificationToken() directly and await the result.
 *
 * On retrieval the controller dispatches "bridge--notification-token:retrieved"
 * with { detail: { token, environment } } (token matches Joe Masilotti's
 * canonical component; environment added for the device registry) so a
 * sibling controller (push_registration_controller.js) can POST it without
 * coupling to this one.
 *
 * Web side adapted from joemasilotti/bridge-components notification_token_controller.js.
 *
 * CAVEAT (provisioning): both native halves are complete; both need credentials the
 * repo does not carry. iOS needs the Push Notifications capability + aps-environment
 * entitlement; Android needs native/android/app/google-services.json. Unprovisioned is
 * a no-op, not a crash — the promise still settles, with "not-provisioned" on Android.
 *
 * Callers must therefore treat a rejection as normal and expected, not exceptional:
 * TOKEN_DENIED is the one worth surfacing, since ChippieBridge.openSettings() is
 * the only way back from it on either platform.
 */

import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

export const TOKEN_NOT_DETERMINED = "not-determined";
export const TOKEN_DENIED = "denied";

// NotificationTokenComponent.swift's "get" reply errors.
const IOS_ERRORS = {
  "notification-authorization-not-determined": TOKEN_NOT_DETERMINED,
  "notification-authorization-denied": TOKEN_DENIED,
};

// MainActivity.fetchNotificationToken's error when the dialog is declined.
const ANDROID_ERRORS = {
  "permission-denied": TOKEN_DENIED,
};

// Android + PWA impls are platform-static (no per-instance bridge needed), so
// register them at module load. connect() upgrades the ios impl to a live send.
function registerStandalone() {
  if (!window.ChippieBridge || !window.ChippieBridge.register) return;
  window.ChippieBridge.register("notificationToken", {
    // ios placeholder: replaced by connect() with a real this.send. If selected
    // before connect (shouldn't happen — facade picks ios only inside the shell),
    // reject so callers don't hang.
    ios: () => Promise.reject(new Error("ios-bridge-not-connected")),
    android: (options) => androidGetToken(options),
    pwa: () => Promise.resolve(null),
  });
}

// Android: bridge a fire-and-forget @JavascriptInterface call to a Promise. The
// native side resolves window.ChippieBridge._resolveNotificationToken(id, token,
// error) once FCM responds; we keep pending resolvers keyed by request id here.
// FCM has no sandbox/production split, so environment is always null.
function androidGetToken(options) {
  if (!window.ChippieNative || !window.ChippieNative.requestNotificationToken) {
    return Promise.resolve(null);
  }
  const interactive = !!(options && options.interactive);
  return (interactive ? Promise.resolve() : androidRequireGrant())
    .then(() => androidRawToken())
    .then((token) => (token ? { token, environment: null } : null));
}

// Non-interactive gate: resolve only when the notification grant is already
// held (always true below Android 13, which has no runtime grant). A shell too
// old to answer the check reports unknown, which rejects the same way.
function androidRequireGrant() {
  return new Promise((resolve, reject) => {
    window.ChippieBridge.permissions("check", "push", (status) => {
      if (status && status.code === 1) resolve();
      else reject(new Error(TOKEN_NOT_DETERMINED));
    });
  });
}

function androidRawToken() {
  return new Promise((resolve, reject) => {
    const requestId = `nt_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const pending = (window.ChippieBridge.__pendingNotificationToken ||= {});
    pending[requestId] = { resolve, reject };
    // Single shared resolver the native layer calls back into.
    if (!window.ChippieBridge._resolveNotificationToken) {
      window.ChippieBridge._resolveNotificationToken = (id, token, error) => {
        const entry = window.ChippieBridge.__pendingNotificationToken?.[id];
        if (!entry) return;
        delete window.ChippieBridge.__pendingNotificationToken[id];
        if (error) entry.reject(new Error(ANDROID_ERRORS[error] || error));
        else entry.resolve(token || null);
      };
    }
    try {
      window.ChippieNative.requestNotificationToken(requestId);
    } catch (e) {
      delete pending[requestId];
      reject(e);
    }
  });
}

if (window.ChippieBridge) {
  registerStandalone();
} else {
  document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
  static component = "notification-token";

  connect() {
    super.connect();
    // Eagerly warm the iOS token so it's cached by the time "get" runs.
    this.send("connect");
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("notificationToken", {
      ios: (options) => this.#iosGetToken(options),
      android: (options) => androidGetToken(options),
      pwa: () => Promise.resolve(null),
    });
  }

  disconnect() {
    // Leaving the iOS shell: drop the live-send impl back to the standalone
    // placeholder so a stale this.send is never invoked.
    registerStandalone();
    super.disconnect();
  }

  // data-action="bridge--notification-token#get" trigger — fetch and dispatch.
  // A button tap is an explicit user gesture, so the OS prompt may fire.
  get(event) {
    if (event) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
    window.ChippieBridge
      .notificationToken({ interactive: true })
      .then((result) => {
        if (result && result.token) {
          this.dispatch("retrieved", {
            detail: {
              token: result.token,
              environment: result.environment || null,
            },
          });
        }
        return result;
      })
      .catch(() => {
        /* denial / failure: swallow — callers can act on the absent dispatch */
      });
  }

  // iOS: send "get" over Strada; resolve when the Swift component replies
  // { token, environment }. environment records which APNs host the build's
  // tokens are valid against (Debug -> sandbox, TestFlight/App Store ->
  // production) — load-bearing for the device registry.
  #iosGetToken(options) {
    const interactive = !!(options && options.interactive);
    return new Promise((resolve, reject) => {
      this.send("get", { interactive }, (message) => {
        const data = message.data || {};
        if (data.error) reject(new Error(IOS_ERRORS[data.error] || data.error));
        else if (!data.token) resolve(null);
        else resolve({ token: data.token, environment: data.environment || null });
      });
    });
  }
}
